import type { StaticImageData } from 'next/image';

import type { ProductTypeOptions } from '@/types/product';

import type { ProductMediaFragment } from '@/graphql/saleor/api';
import { type GetImageDimensions, getImageDimensions } from '@/lib/images';
import type { Maybe } from '@/lib/types';

export interface StaticImageSrcAsUrl {
  url: StaticImageData['src'];
}

export type ProductImage = ProductMediaFragment;
export type CustomImage = StaticImageSrcAsUrl & { alt: string };
export type Media = ProductImage | CustomImage;

export type ProductImageLarge = Pick<ProductMediaFragment, 'url'> &
  Partial<GetImageDimensions>;
export type CustomImageLarge = StaticImageSrcAsUrl &
  Partial<Pick<StaticImageData, 'width' | 'height'>>;
export type MediaLarge = ProductImageLarge | CustomImageLarge;

export type Image = {
  large?: MediaLarge;
  thumb: Media;
};

const WATCH_SHOT_TYPE_ORDER = [
  'Soldier',
  'Three-quarter',
  'Rolled up',
  'Back',
  'On wrist',
  'Lumes',
  'Buckle',
] as const;

const EYEWEAR_SHOT_TYPE_ORDER = [
  'Soldier',
  'Three-quarter',
  'Rolled up',
  'Eyewear Detail 1',
  'Eyewear Detail 2',
  'Eyewear Detail 3',
] as const;

type ShotTypeOrder = readonly string[];

const BACK_SYNONYMS = ['back', 'back engraving'];
const STRAP_SHOT_TYPE = 'strap';

const shouldFilterEmptyShotType = (productType?: ProductTypeOptions) =>
  productType === 'watch' || productType === 'eyewear';

const getShotTypeOrder = (productType?: ProductTypeOptions): ShotTypeOrder =>
  productType === 'eyewear' ? EYEWEAR_SHOT_TYPE_ORDER : WATCH_SHOT_TYPE_ORDER;

const normalizeShotType = (shotType: string | null | undefined) =>
  shotType?.toLowerCase().trim() ?? '';

const findBucket = (normalized: string, order: ShotTypeOrder) =>
  order.find((type) => type.toLowerCase() === normalized);

export const getShotTypeBucket = (
  shotType: string | null | undefined,
  order: ShotTypeOrder,
): string | undefined => {
  if (!shotType) {
    return undefined;
  }

  const normalized = normalizeShotType(shotType);

  if (BACK_SYNONYMS.includes(normalized)) {
    return findBucket('back', order) ?? 'Back';
  }

  return findBucket(normalized, order);
};

export const sortImagesByShotType = (
  images: Image[],
  productType?: ProductTypeOptions,
): Image[] => {
  const sourceImages = shouldFilterEmptyShotType(productType)
    ? images.filter(
        (img) =>
          'shotType' in img.thumb &&
          !!normalizeShotType(img.thumb.shotType)?.length,
      )
    : images;

  if (!sourceImages.length) {
    return [];
  }

  const order = getShotTypeOrder(productType);
  const orderMap = new Map(order.map((type, index) => [type, index]));
  const includeBackLogic = productType === 'watch';
  const backBucket = includeBackLogic ? findBucket('back', order) : undefined;

  const sorted = [...sourceImages].sort((a, b) => {
    const rawShotA =
      'shotType' in a.thumb && a.thumb.shotType ? a.thumb.shotType : '';
    const rawShotB =
      'shotType' in b.thumb && b.thumb.shotType ? b.thumb.shotType : '';

    const bucketA = getShotTypeBucket(rawShotA, order);
    const bucketB = getShotTypeBucket(rawShotB, order);

    const rankA =
      bucketA !== undefined && orderMap.has(bucketA)
        ? orderMap.get(bucketA)!
        : Number.MAX_SAFE_INTEGER;
    const rankB =
      bucketB !== undefined && orderMap.has(bucketB)
        ? orderMap.get(bucketB)!
        : Number.MAX_SAFE_INTEGER;

    if (rankA !== rankB) {
      return rankA - rankB;
    }

    return 0;
  });

  const seenKeys = new Set<string>();
  const seenBackBucket = { current: false };
  const deduped: Image[] = [];

  sorted.forEach((img) => {
    const rawShot =
      'shotType' in img.thumb && img.thumb.shotType
        ? img.thumb.shotType
        : undefined;
    const bucket = getShotTypeBucket(rawShot, order);

    if (includeBackLogic && backBucket && bucket === backBucket) {
      if (seenBackBucket.current) {
        return;
      }
      seenBackBucket.current = true;
      deduped.push(img);
      return;
    }

    const url = 'url' in img.thumb ? img.thumb.url : '';
    const key = `${bucket ?? 'none'}|${url}`;

    if (seenKeys.has(key)) {
      return;
    }

    seenKeys.add(key);
    deduped.push(img);
  });

  return deduped;
};

const filterStrapImages = (images: Image[]): Image[] => {
  const strapImages = images.filter((img) => {
    if ('shotType' in img.thumb && img.thumb.shotType) {
      return normalizeShotType(img.thumb.shotType) === STRAP_SHOT_TYPE;
    }
    return false;
  });

  if (strapImages.length > 0) {
    return strapImages;
  }

  return images.length ? [images[0]] : images;
};

export const buildImages = (
  media: Maybe<Media[]>,
  mediaLarge: Maybe<MediaLarge[]>,
  productType?: ProductTypeOptions,
): Image[] => {
  if (!media?.length) {
    return [];
  }

  const base = media.map((item, i) => {
    if (!mediaLarge) {
      return {
        thumb: item,
      };
    }

    return {
      thumb: item,
      large: { url: mediaLarge[i].url },
    };
  });

  if (productType === 'strap') {
    return filterStrapImages(base);
  }

  return sortImagesByShotType(base, productType);
};

export const getMediaDimensions = async (
  media: Media[],
  mediaLarge: MediaLarge[],
): Promise<Image[]> => {
  const imagesAll = media.map((item, i) => ({
    thumb: item,
    large: mediaLarge[i],
  }));

  return await Promise.all(
    imagesAll.map(async (item) => {
      const dimensions = await getImageDimensions(item.large.url);
      return {
        thumb: item.thumb,
        large: { ...item.large, ...dimensions },
      };
    }),
  );
};
